// Раздел «Благодарности» — признание портала 73: кого отметил заказчик и чем отмечен
// человек. ТОЛЬКО ЧТЕНИЕ.
//
// ДВА СПИСКА, КОТОРЫЕ НЕЛЬЗЯ СВОДИТЬ В ОДИН, И ЭКРАН ПОКАЗЫВАЕТ ИХ ПОРОЗНЬ
// Вкладка «Благодарности» — РЕЕСТР ПИСЕМ (p73g_gratitudes и p73g_gratitude_members).
// Вкладка «Начисления» — СЛЕД в журнале баллов. Связи между ними нет и в источнике:
// номера письма журнал не хранит, verification_id у таких строк пуст. Совпадение по
// людям и минуте у сегодняшних данных есть, но это совпадение, а не ключ — отмеченные
// в ОТКЛОНЁННЫХ письмах не получили ничего, поэтому в реестре людей больше.
// Показать их одной таблицей — значит соврать связью, которой нет.
//
// ОТКЛОНЁННЫЕ ОТДЕЛЬНО ОТ ОДОБРЕННЫХ. По умолчанию видны только одобренные письма:
// отказ в общем списке читался бы как ещё одно спасибо, а оба сегодняшних отказа —
// это дубли. Отклонённые открываются переключателем и всегда сосчитаны в плитках.
//
// ФОТО ПИСЬМА НЕ ПОКАЗЫВАЕТСЯ И ССЫЛКИ НА НЕГО НЕТ. В зеркале лежит только признак,
// что снимок был приложен: сам файл в корзине портала, доступа к ней у ядра нет.
// Ядро ключ наружу не отдаёт вовсе — рисовать ссылку было бы не из чего, и она всё
// равно не открылась бы. Экран говорит «снимок есть, но не здесь» словами.
//
// ПОЧЕМУ ТУТ ЖЕ ЗНАЧКИ. «Какие значки у человека» — часть того же вопроса «чем
// человека отметили», поэтому они на соседней вкладке, а не в другом разделе.
//
// ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО. Баллов. Ядро отдаёт их только по money=1 и помечает такой
// ответ для владельца: балл портала равен рублю, а это заработок людей поимённо. Экран
// по умолчанию их не просит — признание показывают всем, заработок нет. Галочка «показать
// баллы» есть, и открыть её должен BFF (web/bff/rights.py), а не этот файл.
//
// Начислить, подтвердить или отклонить благодарность отсюда нельзя: и то, и другое —
// запись в портал 73.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'

const БАЗА = '/api/grat'
const РАЗМЕР_СТРАНИЦЫ = 50

// ────────────────────────────── транспорт ──────────────────────────────
// Тот же разбор ответа, что в api/client.ts и api/p73g.ts. 400 от ядра здесь означает
// «так спрашивать нельзя» (незнакомый месяц, не назван человек) — это ОТКАЗ, а не
// «ничего не найдено», и превращать его в пустую таблицу нельзя.

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    if (res.status === 403) {
      throw new Error('баллы за благодарности открыты одной учётке владельца: балл '
                      + 'портала равен рублю, и это заработок людей поимённо')
    }
    const msg = (rec.error as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  restricted?: string
  note?: string
}

interface Свежесть { entity: string; last_sync_at: string | null; age_min: number | null }

interface Счётчики {
  letters: number
  letters_approved: number
  letters_rejected: number
  letters_pending: number
  mentions: number
  people_in_registry: number
  people_thanked: number
  customers: number
  customer_unknown: number
  with_photo: number
  with_comment: number
  letters_30d: number
  waited_days_max: number | null
  accruals: number
  people_in_journal: number
  accruals_linked: number
  badges: number
  badges_never: number
  badges_gratitude: number
  awards: number
  badged_people: number
  awards_gratitude: number
  awards_30d: number
  people_active: number
}

interface Справочники {
  freshness: Свежесть[]
  counts: Счётчики
  customers: { customer: string; letters: number; approved: number }[]
  periods: { period: string; letters: number; approved: number }[]
  people: { id: number; name: string; letters_approved: number; badges: number }[]
  caveats: string[]
  missing: string[]
  status_names: Record<string, string>
  source: string
}

/** Кого отметили в письме. Приезжает подзапросом, а не отдельным обращением. */
interface Отмеченный {
  d1_id: number
  full_name: string
  bitrix_user_id: number | null
}

/** Строка реестра — ПИСЬМО, а не человек. Отмеченных в нём бывает несколько. */
interface Письмо {
  d1_id: number
  status: string
  customer: string | null
  comment: string | null
  reject_reason: string | null
  uploader_d1_id: number | null
  uploader_name: string | null
  reviewed_by_name: string | null
  reviewed_at: string | null
  created_at: string | null
  has_photo: boolean
  waited_days: number | null
  members_count: number
  members: Отмеченный[]
}

interface ИтогиПисем {
  letters: number
  approved: number
  rejected: number
  pending: number
  customers: number
  customer_unknown: number
  people: number
  mentions: number
  waited_days_max: number | null
}

/** Строка ЖУРНАЛА НАЧИСЛЕНИЙ. Другое множество, чем письма, — см. шапку файла. */
interface Начисление {
  d1_id: number
  user_d1_id: number
  full_name: string
  bitrix_user_id: number | null
  customer: string | null
  created_at: string | null
  verification_d1_id: number | null
  points?: number
}

interface ИтогиСледа {
  accruals: number
  people: number
  customers: number
  with_verification: number
  points?: number
}

interface СтрокаВыдачи {
  d1_id: number
  user_d1_id: number
  full_name: string
  code: string | null
  badge_name: string | null
  icon: string | null
  earned_at: string | null
  for_gratitude: boolean | null
}

interface СтрокаКаталога {
  d1_id: number
  code: string | null
  name: string | null
  description: string | null
  icon: string | null
  bonus_points: number | null
  for_gratitude: boolean | null
  awarded: number
  people: number
  last_awarded_at: string | null
}

interface Сводка {
  customers: { customer: string | null; letters: number; approved: number
    rejected: number; people: number; first_at: string | null; last_at: string | null }[]
  people: { d1_id: number; full_name: string; letters_approved: number
    letters_rejected: number; badges: number; last_badge_at: string | null }[]
  nobody: { people: number; engineers: number }
}

const useСправочники = () => useQuery<Справочники>({
  queryKey: ['grat', 'refs'],
  queryFn: () => запрос<Справочники>('/refs'),
  ...ОБЩЕЕ,
})

const useПисьма = (ф: Record<string, unknown>) =>
  useQuery<Список<Письмо> & { totals: ИтогиПисем }>({
    queryKey: ['grat', 'list', ф],
    queryFn: () => запрос('/list' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })

const useСлед = (ф: Record<string, unknown>) =>
  useQuery<Список<Начисление> & { totals: ИтогиСледа }>({
    queryKey: ['grat', 'accruals', ф],
    queryFn: () => запрос('/accruals' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })

const useВыдачи = (ф: Record<string, unknown>) =>
  useQuery<Список<СтрокаВыдачи>>({
    queryKey: ['grat', 'awards', ф],
    queryFn: () => запрос('/awards' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })

const useКаталог = (ф: Record<string, unknown>) =>
  useQuery<Список<СтрокаКаталога>>({
    queryKey: ['grat', 'badges', ф],
    queryFn: () => запрос('/badges' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })

const useСводка = () => useQuery<Сводка>({
  queryKey: ['grat', 'summary'],
  queryFn: () => запрос<Сводка>('/summary'),
  ...ОБЩЕЕ,
})

// ────────────────────────────── экран ──────────────────────────────

type Вкладка = 'list' | 'accruals' | 'awards' | 'badges' | 'summary'

export default function Gratitude() {
  const [вкладка, setВкладка] = useState<Вкладка>('list')
  const refs = useСправочники()
  const счёт = refs.data?.counts
  // Позже всех обновившаяся сущность: свежесть раздела — это свежесть самого отставшего
  // из его источников, а не самого свежего.
  const св = useMemo(() => {
    const все = (refs.data?.freshness ?? []).filter((f) => f.age_min !== null)
    return все.length ? все.reduce((a, b) => (a.age_min! > b.age_min! ? a : b)) : undefined
  }, [refs.data])

  return (
    <div>
      <div className="page-head">
        <h1>Благодарности</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${св.age_min! < 1 ? 'только что' : `${Math.round(св.age_min!)} мин назад`}`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        <ЧегоНетВЗеркале список={refs.data?.missing} />
        <Плитки счёт={счёт} />

        {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
            «.filters > * { width: 100% }» ставит кнопки в столбец и занимает ими весь
            первый экран. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {([
            ['list', `Благодарности${счёт ? ` · ${счёт.letters_approved}` : ''}`],
            ['accruals', `Начисления${счёт ? ` · ${счёт.accruals}` : ''}`],
            ['awards', `Чем отмечены${счёт ? ` · ${счёт.awards}` : ''}`],
            ['badges', `Каталог значков${счёт ? ` · ${счёт.badges}` : ''}`],
            ['summary', 'Сводка'],
          ] as [Вкладка, string][]).map(([код, подпись]) => (
            <button key={код} onClick={() => setВкладка(код)}
                    aria-pressed={вкладка === код}
                    style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
              {подпись}
            </button>
          ))}
        </div>

        {вкладка === 'list' && <Письма refs={refs.data} />}
        {вкладка === 'accruals' && <След refs={refs.data} счёт={счёт} />}
        {вкладка === 'awards' && <Выдачи refs={refs.data} />}
        {вкладка === 'badges' && <Каталог />}
        {вкладка === 'summary' && <СводкаБлок />}

        <Оговорки список={refs.data?.caveats} источник={refs.data?.source} />
      </QueryState>
    </div>
  )
}

/**
 * Первый блок экрана, и он про отсутствующее. Стоит выше данных намеренно: короткая
 * лента без этого объяснения читается как «нас редко благодарят», а правда — «письма
 * в зеркало не переносятся, видно только след начисления».
 */
function ЧегоНетВЗеркале({ список }: { список?: string[] }) {
  if (!список?.length) return null
  return (
    <div className="card notice" style={{ marginBottom: 12 }}>
      <b>Письма есть, снимков писем нет</b>
      <div style={{ marginTop: 6 }}>
        Реестр благодарностей портала лежит в зеркале целиком: заказчик, комментарий, кто
        загрузил, кто рассмотрел, отказы и кого отметили. Не переносится только сам
        снимок письма — он остаётся в корзине портала, и открыть его отсюда нельзя ничем.
        Чего нет в зеркале:
      </div>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {список.map((т) => <li key={т}>{т}</li>)}
      </ul>
    </div>
  )
}

function Плитки({ счёт }: { счёт?: Счётчики }) {
  if (!счёт) return null
  const плитки: [string, string, string][] = [
    ['Благодарностей', String(счёт.letters_approved),
     счёт.letters_rejected
       ? `отклонено ещё ${счёт.letters_rejected} — их видно переключателем`
       : 'все письма реестра одобрены'],
    ['Кого поблагодарили', String(счёт.people_thanked),
     `отмечено в письмах всего ${счёт.people_in_registry} — разница те, `
     + 'чьи письма отклонили'],
    ['Заказчиков', String(счёт.customers),
     `за последние 30 дней писем: ${счёт.letters_30d}`],
    ['Начислений в журнале', String(счёт.accruals),
     `на ${pluralN(счёт.people_in_journal, 'человека', 'человек', 'человек')}`
     + ' — это другой список, не письма'],
    ['Значков выдано', String(счёт.awards),
     `${pluralN(счёт.badged_people, 'человек', 'человека', 'человек')} из `
     + `${счёт.people_active} действующих учёток`],
  ]
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
      {плитки.map(([подпись, число, снизу]) => (
        <div key={подпись} className="card" style={{ flex: '1 1 200px' }}>
          <div className="hint">{подпись}</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{число}</div>
          <div className="hint">{снизу}</div>
        </div>
      ))}
    </div>
  )
}

// ────────────────────────────── вкладка «Благодарности» ──────────────────────────────

function Письма({ refs }: { refs?: Справочники }) {
  // Умолчание — только одобренные, как и у ядра. Отклонённые не спрятаны: их число
  // стоит в плитке наверху, и переключатель ниже открывает их одним кликом.
  const [статус, setСтатус] = useState('approved')
  const [период, setПериод] = useState('')
  const [заказчик, setЗаказчик] = useState('')
  const [человек, setЧеловек] = useState('')
  const [start, setStart] = useState(0)

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  const фильтры = useMemo(() => ({
    status: статус,
    period: период || undefined,
    customer: заказчик || undefined,
    engineer: человек ? Number(человек) : undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [статус, период, заказчик, человек, start])

  const q = useПисьма(фильтры)
  const итоги = q.data?.totals

  return (
    <div>
      <FilterBar id="grat-list">
        <div className="f">
          <label htmlFor="grat-status">Какие письма</label>
          <select id="grat-status" value={статус}
                  onChange={(e) => сСброса(setСтатус)(e.target.value)}>
            <option value="approved">одобренные</option>
            <option value="rejected">отклонённые</option>
            <option value="pending">ждут рассмотрения</option>
            <option value="all">все, что есть</option>
          </select>
        </div>
        <div className="f">
          <label htmlFor="grat-period">Месяц</label>
          <select id="grat-period" value={период}
                  onChange={(e) => сСброса(setПериод)(e.target.value)}>
            <option value="">все, что есть</option>
            {(refs?.periods ?? []).map((p) => (
              <option key={p.period} value={p.period}>{p.period} ({p.letters})</option>
            ))}
          </select>
        </div>
        <div className="f">
          <label htmlFor="grat-customer">Заказчик</label>
          <select id="grat-customer" value={заказчик}
                  onChange={(e) => сСброса(setЗаказчик)(e.target.value)}>
            <option value="">любой</option>
            {(refs?.customers ?? []).map((c) => (
              <option key={c.customer} value={c.customer}>{c.customer} ({c.letters})</option>
            ))}
          </select>
        </div>
        <div className="f">
          <label htmlFor="grat-person">Отмечен в письме</label>
          <select id="grat-person" value={человек}
                  onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
            <option value="">все, кого отмечали</option>
            {(refs?.people ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
        </div>
      </FilterBar>

      <QueryState isLoading={q.isLoading} error={q.error}>
        {итоги && (
          <div className="hint" style={{ marginBottom: 8 }}>
            Найдено: {q.data?.total ?? 0}. Строка — письмо, а не человек: отмечено
            в них {pluralN(итоги.people, 'человек', 'человека', 'человек')},
            заказчиков {итоги.customers}.
            {статус === 'approved' && итоги.rejected === 0 && ' Отклонённые скрыты.'}
          </div>
        )}
        <ТаблицаПисем строки={q.data?.rows ?? []} статус={статус} />
        <Страницы всего={q.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </div>
  )
}

function ТаблицаПисем({ строки, статус }: { строки: Письмо[]; статус: string }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">
          {статус === 'pending'
            ? 'Писем, ждущих рассмотрения, нет'
            : 'Под эти фильтры писем нет'}
        </div>
        <div className="empty-hint">
          {статус === 'pending'
            ? 'Очередь модерации на портале пуста: все поданные письма уже рассмотрены.'
            : 'Письмо появляется здесь, как только его подали на портале, — и одобренное, '
              + 'и отклонённое. Попробуйте снять сужение «какие письма».'}
        </div>
      </div>
    )
  }
  return (
    <div className="card">
      {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Заказчик и за что</th>
              <th>Кого отметили</th>
              <th>Состояние</th>
              <th>Подано</th>
              <th className="num">Ждало, дней</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((r) => (
              <tr key={r.d1_id}>
                <td data-label="Заказчик и за что">
                  {r.customer ? fmtText(r.customer) : 'заказчик не назван'}
                  {r.comment && <div className="hint">{r.comment}</div>}
                  <div className="hint">
                    {/* Ссылки на снимок нет и быть не может: ядро отдаёт только признак,
                        сам файл лежит в корзине портала. */}
                    {r.has_photo ? 'снимок письма есть — открыть можно на портале 73'
                                 : 'снимок письма не приложен'}
                    {r.uploader_name ? ` · загрузил ${r.uploader_name}` : ''}
                  </div>
                </td>
                <td data-label="Кого отметили">
                  {r.members.length
                    ? r.members.map((m) => m.full_name).join(', ')
                    : 'никого'}
                  <div className="hint">
                    {pluralN(r.members_count, 'человек', 'человека', 'человек')}
                  </div>
                </td>
                <td data-label="Состояние">
                  {СОСТОЯНИЕ[r.status] ?? r.status}
                  {r.reject_reason && (
                    <div className="hint">причина: {r.reject_reason}</div>
                  )}
                  {r.reviewed_by_name && (
                    <div className="hint">рассмотрел {r.reviewed_by_name}</div>
                  )}
                </td>
                <td data-label="Подано" className="nowrap">{fmtDate(r.created_at)}</td>
                <td className="num" data-label="Ждало, дней">
                  {r.waited_days ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Подписи статусов. Дублируют status_names из ядра — на случай старого ответа. */
const СОСТОЯНИЕ: Record<string, string> = {
  approved: 'Одобрено', rejected: 'Отклонено', pending: 'Ждёт рассмотрения',
}

// ────────────────────────────── вкладка «Начисления» ──────────────────────────────
//
// Отдельная вкладка, а не колонка в письмах. Причина в шапке файла: связать строку
// журнала с письмом нечем, и любая попытка показать их одной таблицей была бы
// выдумыванием связи.

function След({ refs, счёт }: { refs?: Справочники; счёт?: Счётчики }) {
  const [человек, setЧеловек] = useState('')
  const [баллы, setБаллы] = useState(false)
  const [start, setStart] = useState(0)

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  const фильтры = useMemo(() => ({
    engineer: человек ? Number(человек) : undefined,
    money: баллы || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [человек, баллы, start])

  const q = useСлед(фильтры)

  return (
    <div>
      <div className="card hint" style={{ marginBottom: 12 }}>
        Это начисления баллов за благодарности, а <b>не</b> сами письма. Связать строку
        с письмом нечем: номера письма журнал не хранит. Поэтому список отдельный, и
        числа тут своими быть обязаны — сегодня в реестре отмеченных больше, чем
        начислений, потому что за отклонённые письма баллы не начисляли.
      </div>

      <FilterBar id="grat-accruals">
        <div className="f">
          <label htmlFor="grat-acc-person">Человек</label>
          <select id="grat-acc-person" value={человек}
                  onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
            <option value="">все, кому начисляли</option>
            {(refs?.people ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
          <input type="checkbox" checked={баллы}
                 onChange={(e) => сСброса(setБаллы)(e.target.checked)} />
          показать баллы (только владельцу)
        </label>
      </FilterBar>

      <QueryState isLoading={q.isLoading} error={q.error}>
        <div className="hint" style={{ marginBottom: 8 }}>
          Найдено: {q.data?.total ?? 0}
          {счёт ? `. В реестре писем отмечено ${счёт.people_in_registry}, `
                  + `начисления получили ${счёт.people_in_journal}.` : ''}
        </div>
        <ТаблицаСледа строки={q.data?.rows ?? []} баллы={баллы} />
        <Страницы всего={q.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </div>
  )
}

function ТаблицаСледа({ строки, баллы }: { строки: Начисление[]; баллы: boolean }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Начислений за благодарности не нашлось</div>
        <div className="empty-hint">
          Строка появляется в журнале в момент одобрения письма. Если письма есть,
          а начислений нет — значит все они отклонены либо ещё не рассмотрены.
        </div>
      </div>
    )
  }
  return (
    <div className="card">
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Кому начислено</th>
              <th>Заказчик</th>
              <th>Когда</th>
              {баллы && <th className="num">Баллы</th>}
            </tr>
          </thead>
          <tbody>
            {строки.map((r) => (
              <tr key={r.d1_id}>
                <td data-label="Кому начислено">
                  {r.full_name}
                  <div className="hint">
                    {r.bitrix_user_id ? `Bitrix #${r.bitrix_user_id}` : 'нет связи с Bitrix'}
                  </div>
                </td>
                <td data-label="Заказчик">
                  {fmtText(r.customer)}
                  <div className="hint">разобран из текста причины</div>
                </td>
                <td data-label="Когда" className="nowrap">{fmtDate(r.created_at, true)}</td>
                {баллы && <td className="num" data-label="Баллы">{r.points ?? '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ────────────────────────────── вкладка «Чем отмечены» ──────────────────────────────

function Выдачи({ refs }: { refs?: Справочники }) {
  const [человек, setЧеловек] = useState('')
  const [толькоБлаг, setТолькоБлаг] = useState(false)
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  const фильтры = useMemo(() => ({
    engineer: человек ? Number(человек) : undefined,
    gratitude: толькоБлаг || undefined,
    q: поиск || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [человек, толькоБлаг, поиск, start])

  const q = useВыдачи(фильтры)

  return (
    <div>
      <FilterBar id="grat-awards">
        <div className="f">
          <label htmlFor="grat-aw-person">Человек</label>
          <select id="grat-aw-person" value={человек}
                  onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
            <option value="">все, у кого есть значки</option>
            {(refs?.people ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="f">
          <label htmlFor="grat-aw-q">Значок или человек</label>
          <input id="grat-aw-q" value={поиск} placeholder="часть названия"
                 onChange={(e) => сСброса(setПоиск)(e.target.value)} />
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
          <input type="checkbox" checked={толькоБлаг}
                 onChange={(e) => сСброса(setТолькоБлаг)(e.target.checked)} />
          только за благодарности заказчиков
        </label>
      </FilterBar>

      <QueryState isLoading={q.isLoading} error={q.error}>
        <div className="hint" style={{ marginBottom: 8 }}>Найдено: {q.data?.total ?? 0}</div>
        <ТаблицаВыдач строки={q.data?.rows ?? []} />
        <Страницы всего={q.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </div>
  )
}

function ТаблицаВыдач({ строки }: { строки: СтрокаВыдачи[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Под эти фильтры значков не нашлось</div>
        <div className="empty-hint">
          Значок выдаёт портал 73 — за пройденный курс, за серию заходов, за идеальную
          секцию, за первую благодарность от заказчика. Из CRM значок выдать нельзя.
        </div>
      </div>
    )
  }
  return (
    <div className="card">
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Человек</th>
              <th>Значок</th>
              <th>За что</th>
              <th>Когда</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((r) => (
              <tr key={r.d1_id}>
                <td data-label="Человек">{r.full_name}</td>
                <td data-label="Значок">
                  {r.icon ? `${r.icon} ` : ''}{fmtText(r.badge_name)}
                  <div className="hint">{fmtText(r.code)}</div>
                </td>
                <td data-label="За что">
                  {r.for_gratitude ? 'благодарность заказчика' : 'работа на портале'}
                </td>
                <td data-label="Когда" className="nowrap">{fmtDate(r.earned_at, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ────────────────────────────── вкладка «Каталог значков» ──────────────────────────────

function Каталог() {
  const [вид, setВид] = useState('')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  const фильтры = useMemo(() => ({
    kind: вид || undefined,
    q: поиск || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [вид, поиск, start])

  const q = useКаталог(фильтры)

  return (
    <div>
      <FilterBar id="grat-badges">
        <div className="f">
          <label htmlFor="grat-kind">Какие показывать</label>
          <select id="grat-kind" value={вид}
                  onChange={(e) => сСброса(setВид)(e.target.value)}>
            <option value="">все, что есть</option>
            <option value="awarded">только те, что выдавались</option>
            <option value="never">ни разу не выдавались</option>
            <option value="gratitude">за благодарность заказчика</option>
          </select>
        </div>
        <div className="f">
          <label htmlFor="grat-bq">Название значка</label>
          <input id="grat-bq" value={поиск} placeholder="часть названия"
                 onChange={(e) => сСброса(setПоиск)(e.target.value)} />
        </div>
      </FilterBar>

      <QueryState isLoading={q.isLoading} error={q.error}>
        <div className="hint" style={{ marginBottom: 8 }}>Найдено: {q.data?.total ?? 0}</div>
        <ТаблицаКаталога строки={q.data?.rows ?? []} />
        <Страницы всего={q.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </div>
  )
}

function ТаблицаКаталога({ строки }: { строки: СтрокаКаталога[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Под эти фильтры значков не нашлось</div>
        <div className="empty-hint">
          Каталог заводит портал 73. Попробуйте снять сужение «какие показывать» —
          часть значков заведена, но ни разу не выдавалась.
        </div>
      </div>
    )
  }
  return (
    <div className="card">
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Значок</th>
              <th>За что дают</th>
              <th className="num">Людей</th>
              <th className="num">Выдач</th>
              <th>Последний раз</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((r) => (
              <tr key={r.d1_id}>
                <td data-label="Значок">
                  {r.icon ? `${r.icon} ` : ''}{fmtText(r.name)}
                  <div className="hint">{fmtText(r.code)}</div>
                </td>
                <td data-label="За что дают">
                  {fmtText(r.description)}
                  {r.for_gratitude && <div className="hint">за благодарность заказчика</div>}
                </td>
                <td className="num" data-label="Людей">{r.people}</td>
                <td className="num" data-label="Выдач">{r.awarded}</td>
                <td data-label="Последний раз" className="nowrap">
                  {r.awarded ? fmtDate(r.last_awarded_at) : 'ни разу'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ────────────────────────────── вкладка «Сводка» ──────────────────────────────

function СводкаБлок() {
  const q = useСводка()
  const d = q.data
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {d?.nobody && (
        <div className="card hint" style={{ marginBottom: 12 }}>
          Ни значка, ни благодарности нет у{' '}
          {pluralN(d.nobody.people, 'человека', 'человек', 'человек')} с действующей
          учёткой портала, из них инженеров {d.nobody.engineers}. Это не оценка работы:
          у части из них просто нет заходов в портал. Вопрос, на который отвечает это
          число, — до кого поощрение не доходит вовсе.
        </div>
      )}

      <div className="card">
        <div className="hint" style={{ marginBottom: 6 }}>Заказчики, которые благодарили</div>
        {!d?.customers?.length ? (
          <div className="empty">
            <div className="empty-title">Благодарностей от заказчиков пока не видно</div>
            <div className="empty-hint">
              В журнале начислений нет ни одной подтверждённой благодарности. Письма,
              которые ждут подтверждения на портале, в зеркало не попадают.
            </div>
          </div>
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Заказчик</th>
                  <th className="num">Писем</th>
                  <th className="num">Одобрено</th>
                  <th className="num">Отклонено</th>
                  <th className="num">Отмечено людей</th>
                  <th>Последнее</th>
                </tr>
              </thead>
              <tbody>
                {d.customers.map((c) => (
                  <tr key={c.customer ?? 'без названия'}>
                    <td data-label="Заказчик">
                      {c.customer ? fmtText(c.customer) : 'заказчик не назван'}
                    </td>
                    <td className="num" data-label="Писем">{c.letters}</td>
                    <td className="num" data-label="Одобрено">{c.approved}</td>
                    <td className="num" data-label="Отклонено">{c.rejected}</td>
                    <td className="num" data-label="Отмечено людей">{c.people}</td>
                    <td data-label="Последнее" className="nowrap">{fmtDate(c.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="hint" style={{ marginBottom: 6 }}>
          Кого отмечали. Благодарности и значки показаны рядом, а не сложены в один
          рейтинг: сложить письмо заказчика со значком за серию заходов значило бы
          придумать вес, которого никто не назначал.
        </div>
        {!d?.people?.length ? (
          <div className="empty">
            <div className="empty-title">Отмеченных людей не нашлось</div>
            <div className="empty-hint">
              Ни одной благодарности и ни одного значка в зеркале нет — либо синк портала
              73 ещё не проходил.
            </div>
          </div>
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Человек</th>
                  <th className="num">Благодарностей</th>
                  <th className="num">Отклонено</th>
                  <th className="num">Значков</th>
                  <th>Последний значок</th>
                </tr>
              </thead>
              <tbody>
                {d.people.map((p) => (
                  <tr key={p.d1_id}>
                    <td data-label="Человек">{p.full_name}</td>
                    <td className="num" data-label="Благодарностей">
                      {p.letters_approved}
                    </td>
                    <td className="num" data-label="Отклонено">{p.letters_rejected}</td>
                    <td className="num" data-label="Значков">{p.badges}</td>
                    <td data-label="Последний значок" className="nowrap">
                      {fmtDate(p.last_badge_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </QueryState>
  )
}

// ────────────────────────────── общее ──────────────────────────────

function Оговорки({ список, источник }: { список?: string[]; источник?: string }) {
  if (!список?.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Чего на этом экране нет и почему</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {список.map((т) => <li key={т}>{т}</li>)}
      </ul>
      {источник && <div style={{ marginTop: 6 }}>Источник: {источник}</div>}
    </div>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0}
              onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}
